// =============================================================================
// Admin Tax Access Management Page
// =============================================================================

import { useState, useEffect } from 'react';
import { adminService, type TempAccessUser, type DealWithStats } from '../../services/admin.service';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Component
// =============================================================================

export function TempAccessPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canManage = hasPermission('accounting', 'manage');
  const [tempUsers, setTempUsers] = useState<TempAccessUser[]>([]);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<TempAccessUser | null>(null);
  const [updating, setUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; name: string } | null>(null);
  const [revokeConfirmText, setRevokeConfirmText] = useState('');
  const [revoking, setRevoking] = useState(false);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formAccessType, setFormAccessType] = useState<'ACCOUNTANT' | 'AUDITOR' | 'CUSTOM'>('ACCOUNTANT');
  const [formExpiresInDays, setFormExpiresInDays] = useState('30');
  const [formSelectedDeals, setFormSelectedDeals] = useState<string[]>([]);
  const [formAllDeals, setFormAllDeals] = useState(true);
  const [formCanViewSsn, setFormCanViewSsn] = useState(false);
  const [formCanViewBanking, setFormCanViewBanking] = useState(false);
  const [formSendEmail, setFormSendEmail] = useState(true);

  const accessTypes = [
    { value: 'ACCOUNTANT', label: 'Accountant', description: 'Access to K-1s and tax documents' },
    { value: 'AUDITOR', label: 'Auditor', description: 'Full read-only access for auditing purposes' },
    { value: 'CUSTOM', label: 'Custom', description: 'Configure specific permissions' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [users, dealsList] = await Promise.all([
        adminService.listTempAccess(),
        adminService.listDeals(),
      ]);
      setTempUsers(users);
      setDeals(dealsList);
    } catch (err) {
      setError('Failed to load tax access users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formEmail || !formName) {
      setCreateError('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);

      await adminService.createTempAccess({
        email: formEmail,
        name: formName,
        expiresInDays: parseInt(formExpiresInDays, 10),
        accessType: formAccessType,
        dealIds: formAllDeals ? [] : formSelectedDeals, // Empty array = all deals
        canViewSsn: formCanViewSsn,
        canViewBanking: formCanViewBanking,
        sendEmail: formSendEmail,
      });

      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setCreateError(error.response?.data?.error || 'Failed to grant access');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (user: TempAccessUser) => {
    setEditingUser(user);
    // Set form values from user
    setFormAccessType(user.accessType || 'ACCOUNTANT');
    setFormExpiresInDays('30'); // Reset to 30 days when extending
    setFormCanViewSsn(user.canViewSsn || false);
    setFormCanViewBanking(user.canViewBanking || false);
    // If user has specific deals, use those; otherwise assume all deals
    if (user.deals && user.deals.length > 0 && user.deals.length < deals.length) {
      setFormAllDeals(false);
      setFormSelectedDeals(user.deals.map(d => d.id));
    } else {
      setFormAllDeals(true);
      setFormSelectedDeals([]);
    }
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingUser) return;

    try {
      setUpdating(true);
      setEditError(null);

      await adminService.updateTempAccess(editingUser.id, {
        expiresInDays: parseInt(formExpiresInDays, 10),
        dealIds: formAllDeals ? [] : formSelectedDeals,
        canViewSsn: formCanViewSsn,
        canViewBanking: formCanViewBanking,
      });

      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      loadData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setEditError(error.response?.data?.error || 'Failed to update access');
    } finally {
      setUpdating(false);
    }
  };

  const handleRevokeClick = (userId: string, userName: string) => {
    setRevokeTarget({ id: userId, name: userName });
    setRevokeConfirmText('');
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget || revokeConfirmText.toLowerCase() !== 'proceed') return;
    setRevoking(true);
    try {
      await adminService.revokeTempAccess(revokeTarget.id);
      setRevokeTarget(null);
      loadData();
    } catch (err) {
      console.error('Failed to revoke access:', err);
    } finally {
      setRevoking(false);
    }
  };

  const resetForm = () => {
    setFormEmail('');
    setFormName('');
    setFormAccessType('ACCOUNTANT');
    setFormExpiresInDays('30');
    setFormSelectedDeals([]);
    setFormAllDeals(true);
    setFormCanViewSsn(false);
    setFormCanViewBanking(false);
    setFormSendEmail(true);
    setCreateError(null);
    setEditError(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getExpiryStatus = (expiresAt?: string) => {
    if (!expiresAt) return { label: 'No expiry', color: 'gray' };

    const expires = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) return { label: 'Expired', color: 'red' };
    if (daysLeft <= 7) return { label: `${daysLeft} days left`, color: 'yellow' };
    return { label: `${daysLeft} days left`, color: 'green' };
  };

  const toggleDealSelection = (dealId: string) => {
    setFormSelectedDeals((prev) =>
      prev.includes(dealId)
        ? prev.filter((id) => id !== dealId)
        : [...prev, dealId]
    );
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('accounting', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Access</h1>
          <p className="text-gray-500 mt-1">
            Grant tax access to accountants and auditors for K-1 preparation
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Grant Access
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-blue-900">SOC 2 Compliant Access Control</p>
            <p className="text-sm text-blue-700 mt-1">
              All tax access is logged and audited. Access automatically expires after the configured period.
              Users receive email notifications when access is granted.
            </p>
          </div>
        </div>
      </div>

      {/* Active Access List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Active Access Grants</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">{error}</div>
        ) : tempUsers.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-gray-500">No tax access grants</p>
            {canManage && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-primary-600 hover:text-primary-800"
              >
                Grant access to someone
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deals</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Granted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tempUsers.map((user) => {
                  const expiry = getExpiryStatus(user.expiresAt);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {user.dealCount === 0 || !user.deals || user.deals.length === 0 ? (
                            <span className="text-amber-600">No deals assigned</span>
                          ) : user.deals.length === deals.length ? (
                            <span className="text-green-600">All deals ({user.dealCount})</span>
                          ) : (
                            <span className="text-gray-600">
                              {user.deals.slice(0, 2).map(d => d.name).join(', ')}
                              {user.deals.length > 2 && ` +${user.deals.length - 2} more`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {user.expiresAt ? formatDate(user.expiresAt) : 'No expiry'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-sm ${
                          expiry.color === 'green' ? 'bg-green-100 text-green-800' :
                          expiry.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          expiry.color === 'red' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {expiry.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-3">
                        {canManage && (
                          <>
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleRevokeClick(user.id, user.name)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Access Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Grant Tax Access</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {createError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {createError}
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Access Type</label>
                  <div className="space-y-2">
                    {accessTypes.map((type) => (
                      <label
                        key={type.value}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                          formAccessType === type.value ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          value={type.value}
                          checked={formAccessType === type.value}
                          onChange={(e) => setFormAccessType(e.target.value as typeof formAccessType)}
                          className="mt-1 text-primary-600 focus:ring-primary-500"
                        />
                        <div>
                          <p className="font-medium">{type.label}</p>
                          <p className="text-sm text-gray-500">{type.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Access Duration</label>
                  <select
                    value={formExpiresInDays}
                    onChange={(e) => setFormExpiresInDays(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                </div>

                {/* Deal Selection - Always shown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deal Access</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        checked={formAllDeals}
                        onChange={() => setFormAllDeals(true)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium">All Deals ({deals.length})</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        checked={!formAllDeals}
                        onChange={() => setFormAllDeals(false)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium">Select Specific Deals</span>
                    </label>
                  </div>
                  {!formAllDeals && (
                    <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                      {deals.map((deal) => (
                        <label key={deal.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={formSelectedDeals.includes(deal.id)}
                            onChange={() => toggleDealSelection(deal.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm">{deal.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {formAccessType === 'CUSTOM' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sensitive Data Access</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formCanViewSsn}
                          onChange={(e) => setFormCanViewSsn(e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm">Can view SSN/Tax IDs</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formCanViewBanking}
                          onChange={(e) => setFormCanViewBanking(e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm">Can view banking information</span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={formSendEmail}
                    onChange={(e) => setFormSendEmail(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="sendEmail" className="text-sm text-gray-700">
                    Send email invitation to the user
                  </label>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> {formSendEmail ? 'The user will receive an email with login instructions.' : 'The user will NOT receive an email notification.'}{' '}
                    All actions will be logged for audit purposes.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !formEmail || !formName || (!formAllDeals && formSelectedDeals.length === 0)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Granting...
                    </>
                  ) : (
                    'Grant Access'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Access Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold">Edit Tax Access</h2>
                  <p className="text-sm text-gray-500 mt-1">{editingUser.name} ({editingUser.email})</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {editError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {editError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Extend Access By</label>
                  <select
                    value={formExpiresInDays}
                    onChange={(e) => setFormExpiresInDays(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                </div>

                {/* Deal Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deal Access</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        checked={formAllDeals}
                        onChange={() => setFormAllDeals(true)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium">All Deals ({deals.length})</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        checked={!formAllDeals}
                        onChange={() => setFormAllDeals(false)}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium">Select Specific Deals</span>
                    </label>
                  </div>
                  {!formAllDeals && (
                    <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                      {deals.map((deal) => (
                        <label key={deal.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={formSelectedDeals.includes(deal.id)}
                            onChange={() => toggleDealSelection(deal.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm">{deal.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sensitive Data Access</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formCanViewSsn}
                        onChange={(e) => setFormCanViewSsn(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">Can view SSN/Tax IDs</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formCanViewBanking}
                        onChange={(e) => setFormCanViewBanking(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm">Can view banking information</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={updating || (!formAllDeals && formSelectedDeals.length === 0)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {updating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Access Confirmation Modal */}
      {revokeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Revoke Access</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to revoke access for:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {revokeTarget.name}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This user will immediately lose access to the portal. This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm:
              </label>
              <input
                type="text"
                value={revokeConfirmText}
                onChange={(e) => setRevokeConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setRevokeTarget(null);
                  setRevokeConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={revoking}
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeConfirm}
                disabled={revokeConfirmText.toLowerCase() !== 'proceed' || revoking}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {revoking ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Revoking...
                  </>
                ) : (
                  'Revoke Access'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TempAccessPage;
